import { usdFormatter } from '@/lib/formatting';

import { PercentageInput } from '@/components/TransactionModule/LimitMode/PercentageInput';

import type { TriggerOrderType } from '@/constants/advancedOrder';

import { buttons } from '@/styles/buttons';
import { inputStyles } from '@/styles/input';
import { fontStyles } from '@/styles/typography';

import type { PlacedOrder } from './types';
import { getTriggerPercentage } from './utils';

const TRIGGER_ORDER_LABELS = {
  'stop-loss': 'Stop Loss',
  'take-profit': 'Take Profit',
} as const satisfies Record<TriggerOrderType, string>;
const NUMERIC_INPUT_PATTERN = /^\d*\.?\d*$/;

type TriggerOrderRowProps = {
  disabled: boolean;
  isPending: boolean;
  onChangePercentage: (percentage: number) => void;
  onChangePrice: (triggerRate: string | null) => void;
  onClear: () => void;
  orderType: TriggerOrderType;
  outputSymbol: string;
  percentage: number | null;
  placedOrder: PlacedOrder | undefined;
  referenceRate: string;
  sellAmountUsd: number;
  triggerRate: string | null;
};

export function TriggerOrderRow({
  disabled,
  isPending,
  onChangePercentage,
  onChangePrice,
  onClear,
  orderType,
  outputSymbol,
  percentage,
  placedOrder,
  referenceRate,
  sellAmountUsd,
  triggerRate,
}: TriggerOrderRowProps) {
  const label = TRIGGER_ORDER_LABELS[orderType];
  const isPlaced = placedOrder !== undefined;
  const displayedTriggerRate = placedOrder?.triggerRate ?? triggerRate;
  const displayedPercentage =
    placedOrder && referenceRate
      ? getTriggerPercentage(referenceRate, placedOrder.triggerRate)
      : percentage;
  const hasValidSide =
    displayedTriggerRate === null ||
    (orderType === 'take-profit'
      ? Number(displayedTriggerRate) > Number(referenceRate)
      : Number(displayedTriggerRate) < Number(referenceRate));
  const potentialChangeUsd =
    !isPlaced && displayedPercentage !== null && hasValidSide
      ? sellAmountUsd * (displayedPercentage / 100)
      : null;
  const sign = orderType === 'take-profit' ? '+' : '-';
  const colorClass = orderType === 'take-profit' ? 'text-token-green-1000' : 'text-token-red-1000';
  const isRowDisabled = disabled || isPlaced;
  const fieldStyles = inputStyles({
    error: !isPlaced && !hasValidSide,
  });

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,110px)] gap-x-3 gap-y-2">
      <h3
        className={fontStyles({
          variant: 'LABEL_LG',
          class: 'col-span-2',
        })}
      >
        {label}
      </h3>
      <label className="flex flex-col gap-1">
        <span className={fontStyles({ variant: 'LABEL_XS', class: 'text-token-gray-800' })}>
          Price
        </span>
        <div className={fieldStyles.root()}>
          <input
            aria-label={`${label} price`}
            autoComplete="off"
            className={fieldStyles.input({ class: 'tabular-nums' })}
            disabled={isRowDisabled || !referenceRate}
            inputMode="decimal"
            onChange={(event) => {
              const nextValue = event.target.value;
              if (!NUMERIC_INPUT_PATTERN.test(nextValue)) return;
              onChangePrice(nextValue === '' ? null : nextValue);
            }}
            placeholder="0"
            type="text"
            value={displayedTriggerRate ?? ''}
          />
          <span className={fieldStyles.accessoryRight()}>{outputSymbol}</span>
        </div>
      </label>
      <label className="flex flex-col justify-end">
        <PercentageInput
          ariaLabel={`${label} percentage`}
          disabled={isRowDisabled || !referenceRate}
          error={!isPlaced && !hasValidSide}
          icon={<span className={colorClass}>{sign}</span>}
          max={orderType === 'stop-loss' ? 99.99 : Number.POSITIVE_INFINITY}
          min={0}
          onChange={(nextPercentage) => {
            onChangePercentage(orderType === 'take-profit' ? nextPercentage : -nextPercentage);
          }}
          onClear={onClear}
          placeholder="0"
          value={displayedPercentage === null ? null : Math.abs(displayedPercentage)}
          variant="field"
        />
      </label>
      <div className="col-span-2 flex min-h-6 items-center justify-between">
        <span
          className={fontStyles({
            variant: 'LABEL_MD',
            class: isPlaced
              ? 'text-token-green-1000'
              : potentialChangeUsd === null
                ? 'text-token-gray-700'
                : colorClass,
          })}
        >
          {isPlaced
            ? `${label} placed`
            : potentialChangeUsd === null
              ? hasValidSide
                ? ''
                : `${label} must be ${orderType === 'take-profit' ? 'above' : 'below'} market price`
              : `Potential ${orderType === 'take-profit' ? 'profit' : 'loss'}: ${usdFormatter.format(
                  potentialChangeUsd,
                )}`}
        </span>
        {triggerRate !== null && !isPlaced && (
          <button
            className={buttons({
              size: 'extraSmall',
              color: 'transparent',
              shape: 'rounded',
              class: 'text-token-gray-800',
            })}
            disabled={disabled}
            onClick={onClear}
            type="button"
          >
            Clear
          </button>
        )}
        {isPending && (
          <span className="sr-only" role="status">
            Submitting {label}
          </span>
        )}
      </div>
    </div>
  );
}
